import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import type { BlogPostSummary, BlogPillar } from "~/lib/blog-queries";
import { getBlogPostsByLocale, type BlogPost as StaticBlogPost } from "~/content/blog";
import { blogSchema, breadcrumbSchema, schemaGraph } from "~/lib/seo/schema";
import { JsonLd } from "~/components/seo/JsonLd";
import { getLocale } from "~/paraglide/runtime";
import { useState } from "react";
import { serializeForServer } from "~/lib/server-serialization";
import { BlogPostLink } from "~/components/blog/BlogLink";
import { getBlogCanonical, getBlogHeadLinks } from "~/lib/blog-seo";

// ── Server data loader ──────────────────────────────────────────────────────

function mapStaticPost(p: StaticBlogPost): BlogPostSummary {
  return {
    id: p.slug,
    locale: p.locale,
    slug: p.slug,
    title: p.title,
    excerpt: p.description,
    contentType: "editorial" as const,
    pageType: "editorial" as const,
    tags: p.tags,
    author: p.author,
    readingTimeMin: p.readingTimeMin,
    countryCode: null,
    city: null,
    roleCategory: null,
    publishedAt: p.publishedAt as unknown as Date,
    updatedAt: (p.updatedAt ?? p.publishedAt) as unknown as Date,
  };
}

const fetchBlogIndex = createServerFn({ method: "GET" }).handler(async () => {
  // Try DB first, but keep static-only editorial posts visible during DB seeding lag.
  const { listPublishedPosts, listPillars } = await import("~/lib/blog-queries");
  const locale = getLocale();
  const [dbPosts, dbPillars] = await Promise.all([
    listPublishedPosts({ locale, limit: 50 }),
    listPillars(locale),
  ]);
  const staticPosts = locale === "en" || locale === "de" ? getBlogPostsByLocale(locale) : [];

  if (dbPosts.length > 0) {
    const seenRecords = new Set(dbPosts.map((post) => `${post.locale}:${post.slug}`));
    const staticOnlyPosts = staticPosts
      .filter((post) => !seenRecords.has(`${post.locale}:${post.slug}`))
      .map(mapStaticPost);
    const posts = [...dbPosts, ...staticOnlyPosts].sort(
      (a, b) => (b.publishedAt ? new Date(b.publishedAt).getTime() : 0) - (a.publishedAt ? new Date(a.publishedAt).getTime() : 0),
    );
    return serializeForServer({ posts, pillars: dbPillars, locale, source: "db" as const });
  }

  // Fallback: static content (pre-migration)
  const mapped: BlogPostSummary[] = staticPosts.map(mapStaticPost);

  return serializeForServer({ posts: mapped, pillars: [] as BlogPillar[], locale, source: "static" as const });
});

// ── Route ───────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/blog/")({
  loader: () => fetchBlogIndex(),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const canonical = getBlogCanonical({ locale: loaderData.locale, pageType: "index" });
    return {
    meta: [
      { title: "Blog — Career Insights for EU Job Seekers | Alchema" },
      {
        name: "description",
        content:
          "Career guides, salary data, visa requirements, and job market analysis for professionals relocating to Europe. Updated weekly.",
      },
      { property: "og:title", content: "Blog — Career Insights for EU Job Seekers | Alchema" },
      {
        property: "og:description",
        content: "Career guides, salary data, and job market analysis for EU job seekers.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical.absolute },
      { property: "og:image", content: "https://alchema.eu/og-default.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Blog — Career Insights | Alchema" },
      { name: "twitter:description", content: "Career guides and job market analysis for EU job seekers." },
      { name: "twitter:image", content: "https://alchema.eu/og-default.png" },
    ],
    links: getBlogHeadLinks({ locale: loaderData.locale, pageType: "index" }),
  };
  },
  component: BlogIndexPage,
});

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(date: Date | string | null): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// ── Components ──────────────────────────────────────────────────────────────

function PillarNav({ pillars, active, onSelect }: {
  pillars: BlogPillar[];
  active: string | null;
  onSelect: (slug: string | null) => void;
}) {
  if (pillars.length === 0) return null;

  return (
    <nav aria-label="Content pillars" className="flex flex-wrap gap-2">
      <button
        onClick={() => onSelect(null)}
        className="rounded-full px-4 py-1.5 text-[0.8125rem] font-medium transition-colors duration-150"
        style={{
          background: active === null ? "var(--text)" : "transparent",
          color: active === null ? "var(--text-inverse)" : "var(--text-secondary)",
          border: active === null ? "none" : "1px solid var(--border)",
        }}
      >
        All
      </button>
      {pillars.map((p) => (
        <button
          key={p.slug}
          onClick={() => onSelect(p.slug)}
          className="rounded-full px-4 py-1.5 text-[0.8125rem] font-medium transition-colors duration-150"
          style={{
            background: active === p.slug ? "var(--text)" : "transparent",
            color: active === p.slug ? "var(--text-inverse)" : "var(--text-secondary)",
            border: active === p.slug ? "none" : "1px solid var(--border)",
          }}
        >
          {p.title}
        </button>
      ))}
    </nav>
  );
}

function PostCard({ post, index }: { post: BlogPostSummary; index: number }) {
  const isFeature = index === 0;

  return (
    <BlogPostLink
      post={post}
      className="group block"
      style={{
        animationDelay: `${index * 60}ms`,
      }}
    >
      <article
        className={`${isFeature ? "pb-10 mb-2" : "py-7"}`}
        style={{
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        {/* Date + Reading time — quiet metadata line */}
        <div
          className="flex items-center gap-2 text-[0.75rem] tracking-wide uppercase"
          style={{ color: "var(--text-muted)" }}
        >
          <time dateTime={post.publishedAt?.toString()}>
            {formatDate(post.publishedAt)}
          </time>
          {post.readingTimeMin && (
            <>
              <span aria-hidden="true" className="opacity-40">/</span>
              <span>{post.readingTimeMin} min</span>
            </>
          )}
          {post.pageType !== "editorial" && (
            <>
              <span aria-hidden="true" className="opacity-40">/</span>
              <span className="capitalize">{post.pageType} guide</span>
            </>
          )}
        </div>

        {/* Title */}
        <h2
          className={`${isFeature ? "mt-4 text-[clamp(1.5rem,3vw,2.25rem)]" : "mt-3 text-[clamp(1.125rem,2vw,1.375rem)]"} font-semibold leading-tight tracking-[-0.015em] transition-colors duration-150 group-hover:text-[var(--accent)]`}
          style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
        >
          {post.title}
        </h2>

        {/* Excerpt */}
        {post.excerpt && (
          <p
            className={`${isFeature ? "mt-4 text-base" : "mt-2 text-[0.9375rem]"} leading-relaxed`}
            style={{
              color: "var(--text-secondary)",
              maxWidth: "65ch",
            }}
          >
            {post.excerpt}
          </p>
        )}

        {/* Tags — small, understated */}
        {post.tags.length > 0 && (
          <div className={`${isFeature ? "mt-5" : "mt-3"} flex flex-wrap gap-1.5`}>
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full px-2.5 py-0.5 text-[0.6875rem] font-medium"
                style={{
                  background: "var(--surface-raised)",
                  color: "var(--text-muted)",
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Author */}
        {post.author && isFeature && (
          <p
            className="mt-4 text-[0.8125rem]"
            style={{ color: "var(--text-muted)" }}
          >
            By {post.author}
          </p>
        )}
      </article>
    </BlogPostLink>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

function BlogIndexPage() {
  const { posts, pillars, locale } = Route.useLoaderData();
  const [activePillar, setActivePillar] = useState<string | null>(null);

  const filtered = activePillar
    ? posts.filter((p: BlogPostSummary) => p.pillarSlug === activePillar)
    : posts;

  const blogJsonLd = schemaGraph(
    blogSchema(locale),
    breadcrumbSchema([
      { name: "Home", url: locale === "en" ? "/" : `/${locale}` },
      { name: "Blog", url: getBlogCanonical({ locale, pageType: "index" }).path },
    ]),
  );

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={blogJsonLd} />

      {/* Hero section — generous space, no decoration */}
      <section className="mx-auto max-w-3xl px-6 pt-24 pb-8">
        <h1
          className="text-[clamp(2rem,5vw,3.25rem)] font-bold leading-[1.1] tracking-[-0.025em]"
          style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
        >
          Blog
        </h1>
        <p
          className="mt-4 text-[clamp(1rem,2vw,1.125rem)] leading-relaxed"
          style={{ color: "var(--text-secondary)", maxWidth: "52ch" }}
        >
          Career guides, salary data, visa requirements, and job market analysis for professionals building careers in Europe.
        </p>
      </section>

      {/* Pillar filter + Post list */}
      <section className="mx-auto max-w-3xl px-6 pb-32">
        {/* Pillar navigation */}
        {pillars.length > 0 && (
          <div
            className="pb-8 mb-2"
            style={{ borderBottom: "1px solid var(--border-subtle)" }}
          >
            <PillarNav
              pillars={pillars}
              active={activePillar}
              onSelect={setActivePillar}
            />
          </div>
        )}

        {/* Post list — no grid, editorial style */}
        <div>
          {filtered.length === 0 ? (
            <p
              className="py-16 text-center text-sm"
              style={{ color: "var(--text-muted)" }}
            >
              No articles yet. Check back soon.
            </p>
          ) : (
            filtered.map((post: BlogPostSummary, i: number) => (
              <PostCard key={`${post.locale}:${post.slug}`} post={post} index={i} />
            ))
          )}
        </div>

        {/* CTA at bottom — restrained, not a banner */}
        <div className="mt-16 pt-10" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <p
            className="text-[0.9375rem] leading-relaxed"
            style={{ color: "var(--text-secondary)", maxWidth: "48ch" }}
          >
            Looking for an edge in your EU job search? Alchema tailors your resume, cover letter, and applications with AI — built for the European market.
          </p>
          <Link
            to="/register"
            className="mt-5 inline-flex rounded-[10px] px-6 py-2.5 text-sm font-medium transition-colors duration-150"
            style={{
              background: "var(--accent)",
              color: "var(--text-inverse)",
            }}
          >
            Start for free
          </Link>
        </div>
      </section>
    </div>
  );
}
